import { useState, useEffect, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { HiOutlineSelector } from 'react-icons/hi';
import { siteSettings } from '@settings/site-settings';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { getCurrentLocationAndLanguage } from '@utils/location';
export type LanguageOption = {
  id: string;
  name: string;
  value: string;
  icon: JSX.Element;
};
export default function LanguageSwitcher() {
  const {
    site_header,
    defaultLanguage
  } = siteSettings;
  const {
    t
  } = useTranslation('common');
  const options: LanguageOption[] = site_header.languageMenu;
  if (options.length <= 1) {
    return null;
  }
  const router = useRouter();
  const {
    asPath,
    locale
  } = router;
  const initialLanguage: LanguageOption = locale ? options.find(o => o.value === locale) || options[0] : options.find(o => o.value === defaultLanguage) || options[0];
  const [selectedItem, setSelectedItem] = useState<LanguageOption>(initialLanguage);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (!locale) {
      const {
        language
      } = getCurrentLocationAndLanguage();
      router.push(asPath, undefined, {
        locale: language
      });
      Cookies.set('NEXT_LOCALE', language);
      const newSelectedItem = options.find(o => o.value === language) || options[0];
      setSelectedItem(newSelectedItem);
    }
    setIsInitialized(true);
  }, [locale, asPath, router, options]);
  useEffect(() => {
    if (locale) {
      const newSelectedItem = options.find(o => o.value === locale) || options[0];
      setSelectedItem(newSelectedItem);
    }
  }, [locale, options]);
  function handleItemClick(values: LanguageOption) {
    setSelectedItem(values);
    router.push(asPath, undefined, {
      locale: values.value
    }).then(() => {
      Cookies.set('NEXT_LOCALE', values.value);
      router.reload();
    });
  }
  if (!isInitialized) {
    return null;
  }
  return <Listbox value={selectedItem} onChange={handleItemClick} data-sentry-element="Listbox" data-sentry-component="LanguageSwitcher" data-sentry-source-file="language-switcher.tsx">
      {({
      open
    }) => <div className="relative ltr:ml-2 rtl:mr-2 ltr:lg:ml-0 rtl:lg:mr-0 z-10 w-[90px] sm:w-[90px] lg:w-[90px] xl:w-[90px]">
          <Listbox.Button className="border border-neutral-300 dark:border-neutral-800 text-heading dark:text-neutral-200 text-[13px] xl:text-sm font-semibold relative w-full py-2 ltr:pl-3 rtl:pr-3 ltr:pr-7 rtl:pl-7 ltr:text-left rtl:text-right bg-white dark:bg-zinc-950 rounded-lg shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 cursor-pointer">
            <span className="flex truncate items-center">
              <span className="ltr:mr-1.5 rtl:ml-1.5">{selectedItem.icon}</span>{' '}
              {t(selectedItem.name)}
            </span>
            <span className="absolute inset-y-0 ltr:right-0 rtl:left-0 flex items-center ltr:pr-1.5 rtl:pl-1.5 pointer-events-none">
              <HiOutlineSelector className="w-5 h-5 text-neutral-400" aria-hidden="true" />
            </span>
          </Listbox.Button>
          <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options static className="absolute w-full py-1 mt-1 overflow-auto bg-white dark:bg-zinc-950 rounded-md shadow-lg max-h-70 ring-1 ring-black ring-opacity-5 focus:outline-none text-sm">
              {options.map(option => <Listbox.Option key={option.id} className={({
            active
          }) => `${active ? 'text-amber-900 dark:text-amber-200 bg-neutral-100 dark:bg-neutral-800' : 'text-neutral-900 dark:text-neutral-200'}
                      cursor-pointer select-none relative py-2 px-3 hover:bg-neutral-100 dark:hover:bg-neutral-800`} value={option}>
                  {({
              selected,
              active
            }) => <span className="flex items-center">
                      {option.icon}
                      <span className={`${selected ? 'font-medium' : 'font-normal'} block truncate ltr:ml-1.5 rtl:mr-1.5`}>
                        {t(option.name)}
                      </span>
                      {selected ? <span className={`${active && 'text-amber-600'}
                               absolute inset-y-0 ltr:left-0 rtl:right-0 flex items-center ltr:pl-3 rtl:pr-3`} /> : null}
                    </span>}
                </Listbox.Option>)}
            </Listbox.Options>
          </Transition>
        </div>}
    </Listbox>;
}